/**
 * Brimbéloi — Pattern ("Séquence") editor for the storefront.
 *
 * A web port of the app's Pattern screen (pattern_screen.dart,
 * pattern_provider.dart, pattern_block_style.dart, pattern_theme.dart),
 * drawn inside a phone frame by sections/brimbeloi-pattern-editor.liquid.
 * Same blocks (Move / Wait / Loop), same popup presets and limits, same
 * drag & drop in and out of loops, same autosave, and the same file
 * format (version 1 JSON) and Bluetooth encoding (encodeBlocks below).
 *
 * The section's form is Shopify's native contact form: on submit, the
 * hidden contact[...] fields are filled from the pattern, so the team
 * receives it as a store contact email — the ESP32 string first.
 */
(() => {
  if (customElements.get('brimbeloi-pattern-editor')) return;

  const WIP_KEY = 'brimbeloi.pattern.wip.v1';
  const LIBRARY_KEY = 'brimbeloi.pattern.library.v1';
  const DEFAULT_NAME = 'Nouvelle séquence';

  /** How long the video takes to change speed or stop (ms). */
  const RAMP_MS = 500;

  /** The slowest browsers play a video. */
  const MIN_RATE = 0.0625;

  /** The sequence a first-time visitor finds in the phone. */
  const STARTER_PATTERN = {
    version: 1,
    name: 'Séquence de départ',
    blocks: [
      { type: 'move', speed: 75, duration: 3 },
      { type: 'wait', duration: 2 },
      {
        type: 'loop',
        count: 3,
        children: [
          { type: 'move', speed: 100, duration: 1 },
          { type: 'wait', duration: 1 },
        ],
      },
    ],
  };
  const NAME_MAX = 40;

  // ── Storage (best-effort: private mode or blocked storage just skips it) ──

  const storage = {
    get(key) {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch (_) {
        return null;
      }
    },
    set(key, value) {
      try {
        if (value == null) localStorage.removeItem(key);
        else localStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch (_) {
        return false;
      }
    },
  };

  // ── Icons (Material Symbols, rounded where the app uses _rounded) ────────

  const ICONS = {
    speed:
      'M20.38 8.57l-1.23 1.85a8 8 0 0 1-.22 7.58H5.07A8 8 0 0 1 15.58 6.85l1.85-1.23A10 10 0 0 0 3.35 19a2 2 0 0 0 1.72 1h13.85a2 2 0 0 0 1.74-1 10 10 0 0 0-.27-10.44zm-9.79 6.84a2 2 0 0 0 2.83 0l5.66-8.49-8.49 5.66a2 2 0 0 0 0 2.83z',
    hourglass: 'M18 22l-.01-6L14 12l3.99-4.01L18 2H6v6l4 4-4 3.99V22h12zM8 7.5V4h8v3.5l-4 4-4-4z',
    repeat: 'M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z',
    edit:
      'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z',
    copy: 'M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z',
    close: 'M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
    drag:
      'M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    expand: 'M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z',
    timer:
      'M15 1H9v2h6V1zm-4 13h2V8h-2v6zm8.03-6.61 1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42A8.96 8.96 0 0 0 12 4c-4.97 0-9 4.03-9 9s4.02 9 9 9 9-4.03 9-9c0-2.12-.74-4.07-1.97-5.61zM12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z',
    folder:
      'M9.17 6l2 2H20v10H4V6h5.17M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z',
    folderOpen:
      'M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z',
    save: 'M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67 2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z',
    add: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
    remove: 'M19 13H5v-2h14v2z',
    chevron: 'M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z',
    back: 'M17.77 3.77 16 2 6 12l10 10 1.77-1.77L9.54 12z',
    delete: 'M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM8 9h8v10H8V9zm7.5-5-1-1h-5l-1 1H5v2h14V4z',
    check:
      'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    error:
      'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z',
    info: 'M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z',
    warning: 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
  };

  const icon = (name, cls = '') =>
    `<svg class="bpe-ic${cls ? ` ${cls}` : ''}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${ICONS[name]}"/></svg>`;

  const STATUS_ICONS =
    '<svg viewBox="0 0 18 12" aria-hidden="true"><rect x="0" y="8" width="3" height="4" rx="1"/><rect x="5" y="5.5" width="3" height="6.5" rx="1"/><rect x="10" y="3" width="3" height="9" rx="1"/><rect x="15" y="0" width="3" height="12" rx="1"/></svg>' +
    '<svg viewBox="0 0 16 12" aria-hidden="true"><path d="M8 2.4c2.3 0 4.4.9 6 2.4l1.3-1.4A10.6 10.6 0 0 0 8 .5 10.6 10.6 0 0 0 .7 3.4L2 4.8a8.6 8.6 0 0 1 6-2.4Zm0 3.8c1.3 0 2.5.5 3.4 1.3l1.3-1.4A6.8 6.8 0 0 0 8 4.3a6.8 6.8 0 0 0-4.7 1.8l1.3 1.4A4.9 4.9 0 0 1 8 6.2ZM8 8a2.9 2.9 0 0 0-2 .8L8 11l2-2.2A2.9 2.9 0 0 0 8 8Z"/></svg>' +
    '<svg viewBox="0 0 27 12" aria-hidden="true"><rect x=".5" y=".5" width="22" height="11" rx="3.2" fill="none" stroke="currentColor" opacity=".45"/><rect x="2" y="2" width="17" height="8" rx="2"/><path d="M24 4v4a2 2 0 0 0 0-4Z" opacity=".5"/></svg>';

  const escapeHtml = (text) =>
    String(text).replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
    );

  const vibrate = (ms) => {
    try {
      navigator.vibrate?.(ms);
    } catch (_) {}
  };

  // ── Numbers ("3", "2,5" — like formatBlockNumber with the French locale) ──

  const numberFormat = new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 2, useGrouping: false });
  const fmt = (value) => numberFormat.format(value);
  const fmtSeconds = (value) => `${fmt(value)} s`;

  /** Parses a typed-in value, accepting either decimal separator. */
  const parseNumber = (text) => {
    const s = String(text).trim().replace(',', '.');
    if (!/^(\d+\.?\d*|\.\d+)$/.test(s)) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  const fmtDuration = (seconds) => {
    const total = Math.round(seconds * 10) / 10;
    if (total < 60) return fmtSeconds(total);
    const min = Math.floor(total / 60);
    const sec = Math.round((total - min * 60) * 10) / 10;
    return sec ? `${min} min ${fmt(sec)} s` : `${min} min`;
  };

  // ── Block kinds, presets & limits (pattern_block_style.dart) ──────────────

  const KIND = {
    move: {
      title: 'Action',
      caption: 'ACTION',
      icon: 'speed',
      presets: [100, 75, 50],
      presetNames: ['Vitesse rapide', 'Vitesse moyenne', 'Vitesse lente'],
      presetDesc: (v) => `Le moteur tourne à ${v} % de sa vitesse`,
      range: [10, 100],
      customDefault: 100,
      unit: '%',
    },
    wait: {
      title: 'Attente',
      caption: 'ATTENTE',
      icon: 'hourglass',
      presets: [5, 3, 1],
      presetNames: ['Pause longue', 'Pause moyenne', 'Pause courte'],
      presetDesc: (v) => `Le moteur s'arrête ${v} seconde${v > 1 ? 's' : ''}`,
      range: [0.1, 30],
      customDefault: 3,
      unit: 's',
    },
    loop: {
      title: 'Boucle',
      caption: 'RÉPÉTER',
      icon: 'repeat',
      presets: [],
      range: [1, 99],
      customDefault: 3,
      unit: '×',
    },
  };

  /** Quick picks for how long a Move runs (s). */
  const MOVE_TIMES = [1, 3, 5, 7];
  const MOVE_TIME_DEFAULT = 1;
  const MOVE_TIME_RANGE = [0.1, 30];

  const presetIndex = (presets, value) => presets.findIndex((p) => Math.abs(p - value) < 0.001);
  const blockValue = (b) => (b.type === 'move' ? b.speed : b.type === 'wait' ? b.duration : b.count);
  const formatValue = (kind, v) =>
    kind === 'move' ? `${Math.trunc(v)}%` : kind === 'wait' ? fmtSeconds(v) : `×${Math.round(v)}`;

  // ── Block model & serialisation (pattern_provider.dart) ───────────────────

  let lastId = 0;
  const makeMove = (speed = 50, duration = 1) => ({ id: ++lastId, type: 'move', speed, duration });
  const makeWait = (duration = 1) => ({ id: ++lastId, type: 'wait', duration });
  const makeLoop = (count = 3, children = []) => ({ id: ++lastId, type: 'loop', count, children });

  const copyBlock = (b) =>
    b.type === 'move'
      ? makeMove(b.speed, b.duration)
      : b.type === 'wait'
        ? makeWait(b.duration)
        : makeLoop(b.count, b.children.map(copyBlock));

  const blockToJson = (b) =>
    b.type === 'move'
      ? { type: 'move', speed: b.speed, duration: b.duration }
      : b.type === 'wait'
        ? { type: 'wait', duration: b.duration }
        : { type: 'loop', count: b.count, children: b.children.map(blockToJson) };

  const toNumber = (value, [min, max]) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error('Not a number');
    return Math.min(max, Math.max(min, value));
  };

  /** Unknown block types inside an otherwise valid pattern are skipped. */
  const blockFromJson = (j) => {
    if (!j || typeof j !== 'object') return null;
    switch (j.type) {
      case 'move':
        return makeMove(toNumber(j.speed, KIND.move.range), toNumber(j.duration, MOVE_TIME_RANGE));
      case 'wait':
        return makeWait(toNumber(j.duration, KIND.wait.range));
      case 'loop': {
        if (!Array.isArray(j.children)) throw new Error('Bad loop');
        const loop = makeLoop(Math.round(toNumber(j.count, KIND.loop.range)));
        for (const child of j.children) {
          const b = blockFromJson(child);
          if (b) loop.children.push(b);
        }
        return loop;
      }
      default:
        return null;
    }
  };

  /** Throws if [json] isn't a pattern at all. */
  const blocksFromJson = (json) => {
    if (!json || typeof json !== 'object' || !Array.isArray(json.blocks)) throw new Error('Not a pattern');
    return json.blocks.map(blockFromJson).filter(Boolean);
  };

  /** The exact string the app writes to the ESP32 over Bluetooth. */
  const encodeBlocks = (list) => {
    const parts = [];
    for (const b of list) {
      if (b.type === 'move') {
        parts.push(`M,${Math.trunc(b.speed)},${Math.round(b.duration * 1000)}`);
      } else if (b.type === 'wait') {
        parts.push(`W,${Math.round(b.duration * 1000)}`);
      } else {
        parts.push(`L,${b.count}`);
        const inner = encodeBlocks(b.children);
        if (inner) parts.push(...inner.split(';'));
        parts.push('E');
      }
    }
    return parts.join(';');
  };

  const cycleSeconds = (list) =>
    list.reduce((t, b) => t + (b.type === 'loop' ? b.count * cycleSeconds(b.children) : b.duration), 0);

  const countBlocks = (list) =>
    list.reduce((n, b) => n + 1 + (b.type === 'loop' ? countBlocks(b.children) : 0), 0);

  const hasEmptyLoop = (list) =>
    list.some((b) => b.type === 'loop' && (!b.children.length || hasEmptyLoop(b.children)));

  /** "1. Action — 75 % pendant 3 s", loops indented under their header. */
  const describeBlocks = (list, prefix = '') =>
    list.flatMap((b, i) => {
      const n = `${prefix}${i + 1}`;
      if (b.type === 'move') return [`${n}. Action — ${Math.trunc(b.speed)} % pendant ${fmtSeconds(b.duration)}`];
      if (b.type === 'wait') return [`${n}. Attente — ${fmtSeconds(b.duration)}`];
      return [
        `${n}. Boucle — répéter ×${b.count}${b.children.length ? ' :' : ' (vide)'}`,
        ...describeBlocks(b.children, `${n}.`).map((line) => `    ${line}`),
      ];
    });

  // ── Rows (the editor is a single column, like _rowsOf in Dart) ────────────

  const rowId = (row) => (row.foot ? `f${row.loop.id}` : `b${row.block.id}`);

  /**
   * The blocks a run goes through, in order: a loop's blocks once per
   * repeat. Each step also carries the loops around it and which round of
   * each it is. Reads the live lists, so an edit made during a test shows
   * up from the next step on.
   */
  function* runSteps(list, loops = []) {
    for (const block of list) {
      if (block.type === 'loop') {
        for (let round = 1; round <= block.count; round++) {
          yield* runSteps(block.children, [...loops, { loop: block, round }]);
        }
      } else {
        yield { block, loops };
      }
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // The editor
  // ══════════════════════════════════════════════════════════════════════════

  class PatternEditor extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.screen = this.querySelector('[data-screen]');
      if (!this.screen) return;
      this._ready = true;

      this.blocks = [];
      this.name = DEFAULT_NAME;
      /** Open loop per level: 'root' or a loop's id → the open loop's id. */
      this.open = new Map();
      this.rowEls = new Map();
      this.builtOnce = false;
      this.drag = null;
      this.overlays = [];

      this.buildShell();
      this.restore();
      this.bindEvents();
      this.render();
      this.setupForm();
      this.setupTester();
      this.startClock();
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this.flush();
      clearInterval(this._clock);
      window.removeEventListener('pagehide', this._onHide);
      document.removeEventListener('visibilitychange', this._onVisibility);
      window.removeEventListener('pageshow', this._onShow);
      window.removeEventListener('submit', this._onSubmit, true);
      cancelAnimationFrame(this._raf);
      this.stopTest();
      this._ready = false;
    }

    // ── Shell ───────────────────────────────────────────────────────────────

    buildShell() {
      const add = (kind) =>
        `<button type="button" class="bpe-add bpe-add--${kind}" data-act="add" data-kind="${kind}">` +
        `<span class="bpe-add__icon">${icon(KIND[kind].icon)}</span><span class="bpe-add__label">${KIND[kind].title}</span></button>`;
      this.screen.innerHTML = `
        <div class="bpe-island" aria-hidden="true"></div>
        <header class="bpe-appbar">
          <div class="bpe-status" aria-hidden="true">
            <span class="bpe-status__time" data-clock></span>
            <span class="bpe-status__icons">${STATUS_ICONS}</span>
          </div>
          <div class="bpe-toolbar">
            <button type="button" class="bpe-title" data-act="rename" title="Renommer la séquence">
              <span class="bpe-title__text" data-name></span>${icon('edit', 'bpe-title__icon')}
            </button>
            <button type="button" class="bpe-files" data-act="files">${icon('folder')}<span>Fichiers</span></button>
          </div>
        </header>
        <div class="bpe-body">
          <div class="bpe-scroll" data-scroll><div class="bpe-list" data-list></div></div>
          <div class="bpe-empty" data-empty hidden>
            <span class="bpe-empty__icon">${icon('add')}</span>
            <p class="bpe-empty__title">Votre séquence est vide</p>
            <p class="bpe-empty__text">Ajoutez une action, une attente ou une boucle avec les boutons ci-dessous.</p>
          </div>
          <div class="bpe-addbar">${add('move')}${add('wait')}${add('loop')}</div>
        </div>
        <div class="bpe-toasts" data-toasts aria-live="polite"></div>
        <div class="bpe-layer" data-layer></div>
        <div class="bpe-home" aria-hidden="true"></div>`;
      this.list = this.screen.querySelector('[data-list]');
      this.scroller = this.screen.querySelector('[data-scroll]');
      this.emptyEl = this.screen.querySelector('[data-empty]');
      this.nameEl = this.screen.querySelector('[data-name]');
      this.layer = this.screen.querySelector('[data-layer]');
      this.toasts = this.screen.querySelector('[data-toasts]');
    }

    startClock() {
      const clock = this.screen.querySelector('[data-clock]');
      const tick = () => {
        const d = new Date();
        clock.textContent = `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
      };
      tick();
      this._clock = setInterval(tick, 15000);
    }

    // ── Autosave (PatternProvider's work-in-progress save) ──────────────────

    restore() {
      const saved = storage.get(WIP_KEY);
      if (!saved) return this.loadStarterPattern();
      try {
        this.blocks = blocksFromJson(saved);
        this.name = this.cleanName(saved.name) || DEFAULT_NAME;
      } catch (_) {
        this.blocks = [];
        this.name = DEFAULT_NAME;
      }
    }

    /**
     * What a first-time visitor finds in the phone (nothing of theirs saved
     * yet): a simple sequence, with its loop open so its blocks show.
     */
    loadStarterPattern() {
      this.blocks = blocksFromJson(STARTER_PATTERN);
      this.name = STARTER_PATTERN.name;
      const loop = this.blocks.find((b) => b.type === 'loop');
      if (loop) this.open.set('root', loop.id);
    }

    toJson(name = this.name) {
      return { version: 1, name, blocks: this.blocks.map(blockToJson) };
    }

    scheduleSave() {
      clearTimeout(this._save);
      this._save = setTimeout(() => this.flush(), 500);
    }

    flush() {
      clearTimeout(this._save);
      storage.set(WIP_KEY, this.blocks.length ? this.toJson() : null);
    }

    cleanName(name) {
      return typeof name === 'string' ? name.trim().slice(0, NAME_MAX) : '';
    }

    changed() {
      this.render();
      this.scheduleSave();
    }

    // ── Events ──────────────────────────────────────────────────────────────

    bindEvents() {
      this.screen.addEventListener('click', (e) => this.onClick(e));
      this.screen.addEventListener('pointerdown', (e) => this.onPointerDown(e));
      this.screen.addEventListener('keydown', (e) => this.onKeyDown(e));
      this._onHide = () => this.flush();
      this._onVisibility = () => document.visibilityState === 'hidden' && this.flush();
      window.addEventListener('pagehide', this._onHide);
      document.addEventListener('visibilitychange', this._onVisibility);
    }

    onClick(e) {
      if (this._suppressClick || this.drag) return;
      if (e.target.closest('[data-grip]')) return;
      const el = e.target.closest('[data-act]');
      if (!el || el.closest('[data-layer]')) return;
      const ref = el.dataset.id ? this.find(Number(el.dataset.id)) : null;
      switch (el.dataset.act) {
        case 'rename':
          return this.rename();
        case 'files':
          return this.showFiles();
        case 'add':
          return this.addBlock(el.dataset.kind);
        case 'add-in':
          return this.addToLoop(Number(el.dataset.loop), el.dataset.kind);
        case 'edit':
          return ref && this.editBlock(ref);
        case 'dup':
          return ref && this.duplicate(ref);
        case 'del':
          return ref && this.remove(ref);
        case 'toggle':
          return ref && this.toggle(ref);
      }
    }

    onKeyDown(e) {
      if (e.key === 'Escape' && this.overlays.length) {
        e.preventDefault();
        this.overlays[this.overlays.length - 1].dismiss();
        return;
      }
      if (e.key === 'Tab' && this.overlays.length) {
        this.trapFocus(e, this.overlays[this.overlays.length - 1].el);
        return;
      }
      // Keyboard reordering from a block's grip.
      const grip = e.target.closest?.('[data-grip]');
      if (grip && (e.key === 'ArrowUp' || e.key === 'ArrowDown') && !this.drag) {
        e.preventDefault();
        const ref = this.find(Number(grip.dataset.grip));
        if (!ref) return;
        const from = ref.list.indexOf(ref.block);
        const to = from + (e.key === 'ArrowUp' ? -1 : 1);
        if (to < 0 || to >= ref.list.length) return;
        ref.list.splice(from, 1);
        ref.list.splice(to, 0, ref.block);
        this.changed();
        this.screen.querySelector(`[data-grip="${ref.block.id}"]`)?.focus();
      }
    }

    trapFocus(e, root) {
      const items = [...root.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])')].filter(
        (el) => !el.disabled && el.offsetParent !== null
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // ── Lookups ─────────────────────────────────────────────────────────────

    /** The block with [id], the list holding it and the loops around it. */
    find(id, list = this.blocks, loops = []) {
      for (const block of list) {
        if (block.id === id) return { block, list, loops };
        if (block.type === 'loop') {
          const found = this.find(id, block.children, [...loops, block]);
          if (found) return found;
        }
      }
      return null;
    }

    parentKey(loops) {
      const parent = loops[loops.length - 1];
      return parent ? parent.id : 'root';
    }

    isOpen(loop, loops) {
      return this.open.get(this.parentKey(loops)) === loop.id;
    }

    /** A new loop opens, ready to be filled. */
    openIfLoop(block, loops) {
      if (block.type === 'loop') this.open.set(this.parentKey(loops), block.id);
    }

    // ── Mutations ───────────────────────────────────────────────────────────

    async addBlock(kind) {
      const choice = await this.blockEditor(kind);
      if (!choice) return;
      const block = this.newBlock(kind, choice);
      this.blocks.push(block);
      this.openIfLoop(block, []);
      this.changed();
      requestAnimationFrame(() => this.scroller.scrollTo({ top: this.scroller.scrollHeight, behavior: 'smooth' }));
    }

    async addToLoop(loopId, kind) {
      const ref = this.find(loopId);
      if (!ref || ref.block.type !== 'loop') return;
      const choice = await this.blockEditor(kind);
      if (!choice) return;
      ref.block.children.push(this.newBlock(kind, choice));
      this.changed();
      // Keep the loop's add buttons in view under the block just added.
      requestAnimationFrame(() => this.revealRow(`f${loopId}`));
    }

    revealRow(id) {
      const el = this.rowEls.get(id);
      if (!el) return;
      const s = this.scroller.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      const bottomLimit = s.bottom - 96;
      if (r.bottom > bottomLimit) {
        this.scroller.scrollBy({ top: r.bottom - bottomLimit, behavior: 'smooth' });
      } else if (r.top < s.top) {
        this.scroller.scrollBy({ top: r.top - s.top - 8, behavior: 'smooth' });
      }
    }

    newBlock(kind, choice) {
      if (kind === 'move') return makeMove(choice.value, choice.time ?? MOVE_TIME_DEFAULT);
      if (kind === 'wait') return makeWait(choice.value);
      return makeLoop(Math.round(choice.value));
    }

    async editBlock({ block }) {
      const choice = await this.blockEditor(block.type, {
        value: blockValue(block),
        time: block.type === 'move' ? block.duration : null,
      });
      if (!choice) return;
      if (block.type === 'move') {
        block.speed = choice.value;
        block.duration = choice.time ?? block.duration;
      } else if (block.type === 'wait') {
        block.duration = choice.value;
      } else {
        block.count = Math.round(choice.value);
      }
      this.changed();
    }

    duplicate({ block, list, loops }) {
      const at = list.indexOf(block);
      if (at < 0) return;
      const copy = copyBlock(block);
      list.splice(at + 1, 0, copy);
      this.openIfLoop(copy, loops);
      this.changed();
    }

    async remove({ block, list }) {
      // A loop can take a whole subtree with it — confirm first.
      if (block.type === 'loop' && block.children.length) {
        const n = countBlocks(block.children);
        const ok = await this.confirmDialog({
          icon: 'delete',
          tone: 'danger',
          title: 'Supprimer cette boucle?',
          body: `La boucle et ${n > 1 ? `les ${n} blocs qu'elle contient seront supprimés` : 'le bloc qu’elle contient seront supprimés'}.`,
          confirm: 'Supprimer',
        });
        if (!ok) return;
      }
      const at = list.indexOf(block);
      if (at < 0) return;
      list.splice(at, 1);
      this.changed();
    }

    toggle({ block, loops }) {
      const key = this.parentKey(loops);
      if (this.open.get(key) === block.id) this.open.delete(key);
      else this.open.set(key, block.id);
      this.changed();
    }

    replaceAll(name, blocks) {
      this.blocks = blocks;
      this.name = name || DEFAULT_NAME;
      this.open.clear();
      this.changed();
    }

    // ── Rendering ───────────────────────────────────────────────────────────

    rowsOf() {
      const rows = [];
      const drag = this.drag;
      // While a block is dragged, its own row sits at the spot it would land.
      const spot = (list, before, loops) => {
        if (drag && drag.slot.list === list && drag.slot.before === before) {
          rows.push({ block: drag.block, list: drag.from, loops, ghost: true });
        }
      };
      const addAll = (list, loops) => {
        for (const block of list) {
          if (drag && block === drag.block) continue;
          spot(list, block, loops);
          rows.push({ block, list, loops });
          if (block.type === 'loop' && this.isOpen(block, loops)) {
            addAll(block.children, [...loops, block]);
            rows.push({ foot: true, loop: block, loops });
          }
        }
        spot(list, null, loops);
      };
      addAll(this.blocks, []);
      return rows;
    }

    render() {
      this.nameEl.textContent = this.name;
      this.emptyEl.hidden = this.blocks.length > 0;
      this.scroller.hidden = this.blocks.length === 0;

      const rows = this.rowsOf();
      // Where every row is drawn right now — the slides below start there.
      const shownBefore = new Map();
      if (this.builtOnce) {
        for (const [id, el] of this.rowEls) shownBefore.set(id, el.getBoundingClientRect().top);
      }

      const els = new Map();
      const order = [];
      for (const row of rows) {
        const id = rowId(row);
        let el = this.rowEls.get(id);
        if (!el) {
          el = document.createElement('div');
          el.dataset.row = id;
        }
        const html = this.rowHtml(row);
        if (el._html !== html) {
          el.innerHTML = html;
          el._html = html;
        }
        el.className = `bpe-row${row.loops.length ? ' is-nested' : ''}${row.ghost ? ' is-ghost' : ''}${
          !row.foot && !row.ghost && this.runningIds?.has(row.block.id) ? ' is-running' : ''
        }`;
        els.set(id, el);
        order.push(el);
      }
      for (const [id, el] of this.rowEls) if (!els.has(id)) el.remove();
      order.forEach((el, i) => {
        if (this.list.children[i] !== el) this.list.insertBefore(el, this.list.children[i] || null);
      });
      this.rowEls = els;
      this.updateSummary();
      this.updateTester();

      if (!this.builtOnce) {
        this.builtOnce = true;
        return;
      }
      this.slideRows(order, shownBefore);
    }

    /**
     * Each row slides from where it was drawn to its new place instead of
     * jumping; a new row comes out from under the one above it and fades in;
     * a block just dropped slides in from where it was let go.
     */
    slideRows(order, shownBefore) {
      const landing = this._landing;
      for (const el of order) {
        el.style.transition = 'none';
        el.style.transform = '';
      }
      const moves = [];
      let aboveBottom = null;
      for (const el of order) {
        const id = el.dataset.row;
        const top = el.getBoundingClientRect().top;
        let shown;
        if (landing && landing.id === id) {
          shown = landing.top;
        } else if (shownBefore.has(id)) {
          shown = shownBefore.get(id);
        } else {
          shown = aboveBottom ?? top;
          el.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
        }
        if (Math.abs(shown - top) > 0.5) moves.push([el, shown - top]);
        aboveBottom = shown + el.offsetHeight;
      }
      if (!moves.length) return;
      for (const [el, dy] of moves) el.style.transform = `translateY(${dy}px)`;
      void this.list.offsetHeight;
      for (const [el] of moves) {
        el.style.transition = 'transform 220ms cubic-bezier(0.215, 0.61, 0.355, 1)';
        el.style.transform = '';
      }
    }

    rowHtml(row) {
      let html;
      if (row.foot) {
        html = this.footHtml(row.loop);
      } else if (row.block.type === 'loop') {
        // A loop being dragged shows closed, wherever it would land.
        const isOpen = !row.ghost && this.isOpen(row.block, row.loops);
        html = `<div class="bpe-block${isOpen ? ' is-open' : ''}">${this.loopHeaderHtml(row.block, isOpen)}</div>`;
      } else {
        html = `<div class="bpe-block">${this.tileHtml(row.block)}</div>`;
      }
      // Each row inside a loop paints its slice of the loop's frame.
      for (let i = 0; i < row.loops.length; i++) html = `<div class="bpe-slice">${html}</div>`;
      return html;
    }

    gripHtml(block) {
      return `<button type="button" class="bpe-grip" data-grip="${block.id}" aria-label="Déplacer le bloc (flèches haut et bas)" title="Glisser pour déplacer">${icon('drag')}</button>`;
    }

    actionsHtml(block) {
      return `<span class="bpe-actions">
        <button type="button" class="bpe-action bpe-action--edit" data-act="edit" data-id="${block.id}" aria-label="Modifier le bloc" title="Modifier">${icon('edit')}</button>
        <button type="button" class="bpe-action" data-act="dup" data-id="${block.id}" aria-label="Dupliquer le bloc" title="Dupliquer">${icon('copy')}</button>
        <button type="button" class="bpe-action" data-act="del" data-id="${block.id}" aria-label="Supprimer le bloc" title="Supprimer">${icon('close')}</button>
      </span>`;
    }

    tileHtml(block) {
      const kind = block.type;
      const k = KIND[kind];
      const value = blockValue(block);
      const preset = presetIndex(k.presets, value);
      // A preset's name already says what it is; custom values fall back to
      // the block type.
      const caption = preset < 0 ? k.caption : k.presetNames[preset].toUpperCase();
      const time =
        kind === 'move' ? `<span class="bpe-timetag">${icon('timer')}${fmtSeconds(block.duration)}</span>` : '';
      return `<div class="bpe-tile bpe-tile--${kind}" data-act="edit" data-id="${block.id}">
        ${this.gripHtml(block)}
        <div class="bpe-main">
          <span class="bpe-badge bpe-badge--${kind}">${icon(k.icon)}</span>
          <span class="bpe-label">
            <span class="bpe-caption">${caption}</span>
            <span class="bpe-valuerow"><span class="bpe-value">${formatValue(kind, value)}</span>${time}</span>
          </span>
        </div>
        ${this.actionsHtml(block)}
      </div>`;
    }

    loopHeaderHtml(block, isOpen) {
      let dots = '';
      if (!isOpen && block.children.length) {
        const shown = block.children.length > 9 ? 7 : block.children.length;
        dots = `<span class="bpe-dots" aria-label="${countBlocks(block.children)} bloc(s) dans la boucle">${block.children
          .slice(0, shown)
          .map((b) => `<span class="bpe-dot bpe-dot--${b.type}"></span>`)
          .join('')}${shown < block.children.length ? `<span class="bpe-dots__more">+${block.children.length - shown}</span>` : ''}</span>`;
      }
      return `<div class="bpe-loophead${isOpen ? ' is-open' : ''}" data-act="toggle" data-id="${block.id}">
          ${this.gripHtml(block)}
          <div class="bpe-main">
            <span class="bpe-badge bpe-badge--wash">${icon('repeat')}</span>
            <span class="bpe-label">
              <span class="bpe-caption">${KIND.loop.caption}</span>
              <span class="bpe-valuerow"><span class="bpe-value">${formatValue('loop', block.count)}</span>${dots}</span>
            </span>
            <button type="button" class="bpe-chevron" data-act="toggle" data-id="${block.id}" aria-expanded="${isOpen}" aria-label="${isOpen ? 'Fermer' : 'Ouvrir'} la boucle">${icon('expand')}</button>
          </div>
          ${this.actionsHtml(block)}
        </div>${isOpen ? '<div class="bpe-bodyslice"></div>' : ''}`;
    }

    footHtml(loop) {
      const small = (kind, label) =>
        `<button type="button" class="bpe-small bpe-small--${kind}" data-act="add-in" data-kind="${kind}" data-loop="${loop.id}">${icon(KIND[kind].icon)}<span>${label}</span></button>`;
      return `<div class="bpe-foot"><div class="bpe-foot__frame"><div class="bpe-foot__tray">
        ${loop.children.length ? '' : '<p class="bpe-foot__hint">Ajoutez les blocs à répéter dans cette boucle.</p>'}
        <div class="bpe-foot__buttons">${small('move', '+ Action')}${small('wait', '+ Attente')}</div>
      </div></div></div>`;
    }

    // ── Drag & drop ─────────────────────────────────────────────────────────
    // A block is dragged by its grip. It floats under the finger while the
    // rows slide apart to open its landing spot — inside an open loop, the
    // loop just stretches to make room.

    onPointerDown(e) {
      const grip = e.target.closest('[data-grip]');
      if (!grip || this.drag || this.overlays.length || (e.pointerType === 'mouse' && e.button !== 0)) return;
      const ref = this.find(Number(grip.dataset.grip));
      const rowEl = ref && this.rowEls.get(`b${ref.block.id}`);
      if (!rowEl) return;
      e.preventDefault();
      const rect = rowEl.getBoundingClientRect();
      const at = ref.list.indexOf(ref.block);
      this.drag = {
        block: ref.block,
        from: ref.list,
        slot: { list: ref.list, before: ref.list[at + 1] ?? null, loops: ref.loops },
        grab: { x: e.clientX - rect.left, y: e.clientY - rect.top },
        size: { w: rect.width, h: rect.height },
        pointer: { x: e.clientX, y: e.clientY },
        pointerId: e.pointerId,
      };
      try {
        this.screen.setPointerCapture(e.pointerId);
      } catch (_) {}
      this._onMove = (ev) => ev.pointerId === this.drag?.pointerId && this.moveDrag(ev);
      this._onUp = (ev) => ev.pointerId === this.drag?.pointerId && this.drop();
      this.screen.addEventListener('pointermove', this._onMove);
      this.screen.addEventListener('pointerup', this._onUp);
      this.screen.addEventListener('pointercancel', this._onUp);
      this.screen.addEventListener('lostpointercapture', this._onUp);
      this.screen.classList.add('is-dragging');
      vibrate(15);

      this.floating = document.createElement('div');
      this.floating.className = 'bpe-floating';
      this.screen.appendChild(this.floating);
      this.setFloatingDepth(ref.loops.length);
      this.render();
      this.placeFloating();
      this.autoScroll();
    }

    setFloatingDepth(depth) {
      const block = this.drag.block;
      const inner =
        block.type === 'loop'
          ? `<div class="bpe-block">${this.loopHeaderHtml(block, false)}</div>`
          : `<div class="bpe-block">${this.tileHtml(block)}</div>`;
      this.floating.classList.toggle('is-nested', depth > 0);
      this.floating.style.setProperty('--depth', depth);
      if (!this.floating.firstChild) this.floating.innerHTML = `<div class="bpe-floating__pad">${inner}</div>`;
    }

    placeFloating() {
      const d = this.drag;
      const screen = this.screen.getBoundingClientRect();
      const list = this.list.getBoundingClientRect();
      this.floating.style.width = `${list.width}px`;
      this.floating.style.transform = `translate(${list.left - screen.left}px, ${d.pointer.y - d.grab.y - screen.top}px)`;
    }

    moveDrag(e) {
      this.drag.pointer = { x: e.clientX, y: e.clientY };
      this.placeFloating();
      this.findSlot();
    }

    /**
     * Moves the ghost to the landing spot nearest the middle of the dragged
     * block. Spots are measured as the list would be without the block, so
     * the choice depends only on where the finger is.
     */
    findSlot() {
      const d = this.drag;
      if (!d) return;
      const middle = d.pointer.y - d.grab.y + d.size.h / 2;
      let nearest = null;
      let best = Infinity;
      const consider = (slot, y) => {
        // The Jiggeur runs a single level of loop, so a loop never lands
        // inside another one (one already nested can still move within its
        // own loop or out of it).
        if (d.block.type === 'loop' && slot.list !== this.blocks && slot.list !== d.from) return;
        const distance = Math.abs(middle - y);
        if (distance < best) {
          best = distance;
          nearest = slot;
        }
      };
      let y = this.list.getBoundingClientRect().top;
      for (const row of this.rowsOf()) {
        const el = this.rowEls.get(rowId(row));
        if (!el || row.ghost) continue;
        if (row.foot) consider({ list: row.loop.children, before: null, loops: [...row.loops, row.loop] }, y);
        else consider({ list: row.list, before: row.block, loops: row.loops }, y);
        y += el.offsetHeight;
      }
      consider({ list: this.blocks, before: null, loops: [] }, y);
      if (!nearest || (nearest.list === d.slot.list && nearest.before === d.slot.before)) return;
      d.slot = nearest;
      vibrate(5);
      this.setFloatingDepth(nearest.loops.length);
      this.render();
    }

    autoScroll() {
      const step = () => {
        const d = this.drag;
        if (!d) return;
        const box = this.scroller.getBoundingClientRect();
        const top = d.pointer.y - d.grab.y;
        const bottom = top + d.size.h;
        const edge = 36;
        let v = 0;
        if (top < box.top + edge) v = top - (box.top + edge);
        else if (bottom > box.bottom - 80) v = bottom - (box.bottom - 80);
        if (v) {
          const before = this.scroller.scrollTop;
          this.scroller.scrollTop += Math.max(-16, Math.min(16, v * 0.3));
          if (this.scroller.scrollTop !== before) this.findSlot();
        }
        this._raf = requestAnimationFrame(step);
      };
      this._raf = requestAnimationFrame(step);
    }

    drop() {
      const d = this.drag;
      if (!d) return;
      cancelAnimationFrame(this._raf);
      this.screen.removeEventListener('pointermove', this._onMove);
      this.screen.removeEventListener('pointerup', this._onUp);
      this.screen.removeEventListener('pointercancel', this._onUp);
      this.screen.removeEventListener('lostpointercapture', this._onUp);
      try {
        this.screen.releasePointerCapture(d.pointerId);
      } catch (_) {}
      this.drag = null;
      this.floating?.remove();
      this.floating = null;
      this.screen.classList.remove('is-dragging');

      const from = d.from.indexOf(d.block);
      if (from >= 0) {
        d.from.splice(from, 1);
        const at = d.slot.before ? d.slot.list.indexOf(d.slot.before) : -1;
        d.slot.list.splice(at < 0 ? d.slot.list.length : at, 0, d.block);
      }
      this._landing = { id: `b${d.block.id}`, top: d.pointer.y - d.grab.y };
      this.changed();
      this._landing = null;
      this._suppressClick = true;
      setTimeout(() => (this._suppressClick = false), 80);
    }

    // ── Overlays (dialogs and sheets drawn inside the phone screen) ─────────

    pushOverlay(el, onDismiss) {
      const previous = document.activeElement;
      const entry = {
        el,
        dismiss: onDismiss,
        close: () => {
          const i = this.overlays.indexOf(entry);
          if (i >= 0) this.overlays.splice(i, 1);
          el.classList.add('is-leaving');
          setTimeout(() => el.remove(), 180);
          if (previous && previous.isConnected && this.screen.contains(previous)) previous.focus({ preventScroll: true });
        },
      };
      this.overlays.push(entry);
      this.layer.appendChild(el);
      el.addEventListener('click', (e) => {
        if (e.target === el) entry.dismiss();
      });
      requestAnimationFrame(() => {
        const first = [...el.querySelectorAll('input, button')].find((n) => !n.disabled && n.offsetParent !== null);
        first?.focus({ preventScroll: true });
      });
      return entry;
    }

    overlay(className = '') {
      const el = document.createElement('div');
      el.className = `bpe-overlay ${className}`;
      return el;
    }

    // ── Block popup (block_editor_dialog) ───────────────────────────────────
    // A block's setting is chosen before it ever lands in the list — closing
    // the popup with ✕ cancels the add outright.

    blockEditor(kind, initial = null) {
      return new Promise((resolve) => {
        const k = KIND[kind];
        const [min, max] = k.range;
        const isMove = kind === 'move';
        const isLoop = kind === 'loop';
        const st = { opt: 1, time: MOVE_TIME_DEFAULT };
        let value = isLoop ? k.customDefault : k.presets[1];
        if (initial) {
          value = initial.value;
          if (isMove) st.time = initial.time ?? MOVE_TIME_DEFAULT;
          const p = presetIndex(k.presets, value);
          st.opt = p >= 0 && (!isMove || presetIndex(MOVE_TIMES, st.time) >= 0) ? p : 'custom';
        }
        if (isLoop) st.opt = 'custom';
        const customStart = st.opt === 'custom' ? value : k.customDefault;
        const uid = `bpe-d${++lastId}`;

        const field = (name, label, unit, start, range, decimals) => `
          <label class="bpe-num">
            <span class="bpe-num__label">${label}</span>
            <span class="bpe-num__box">
              <input type="text" inputmode="${decimals ? 'decimal' : 'numeric'}" autocomplete="off" data-input="${name}" value="${fmt(start)}" aria-describedby="${uid}-${name}">
              <span class="bpe-num__unit">${unit}</span>
            </span>
            <span class="bpe-num__msg" id="${uid}-${name}" data-msg="${name}">Entre ${fmt(range[0])} et ${fmt(range[1])} ${unit === '×' ? 'fois' : unit}</span>
          </label>`;

        const options = k.presets
          .map(
            (v, i) => `<button type="button" class="bpe-opt" role="radio" data-opt="${i}">
              <span class="bpe-radio"></span>
              <span class="bpe-opt__text"><span class="bpe-opt__name">${k.presetNames[i]}</span><span class="bpe-opt__desc">${k.presetDesc(v)}</span></span>
              <span class="bpe-opt__value">${formatValue(kind, v)}</span>
            </button>`
          )
          .join('');

        const el = this.overlay();
        el.innerHTML = `
          <div class="bpe-dialog bpe-editor bpe-editor--${kind}" role="dialog" aria-modal="true" aria-labelledby="${uid}-t">
            <div class="bpe-dhead">
              <span class="bpe-badge bpe-badge--${kind}">${icon(k.icon)}</span>
              <div class="bpe-dhead__text">
                <p class="bpe-dtitle" id="${uid}-t">${k.title}</p>
                <p class="bpe-dsub">${isMove ? 'Choisissez la durée et la vitesse' : kind === 'wait' ? 'Choisissez la durée de la pause' : 'Combien de fois répéter les blocs de la boucle'}</p>
              </div>
              <button type="button" class="bpe-close" data-x aria-label="Fermer">${icon('close')}</button>
            </div>
            ${isMove ? `<p class="bpe-dlabel">Durée de l'action</p><div class="bpe-chips" role="radiogroup" aria-label="Durée de l'action" data-chips></div>` : ''}
            ${
              isLoop
                ? `<p class="bpe-dlabel">Nombre de répétitions</p>
                   <div class="bpe-stepper">
                     <button type="button" class="bpe-step" data-step="-1" aria-label="Moins">${icon('remove')}</button>
                     ${field('value', 'Répétitions', '×', customStart, k.range, false)}
                     <button type="button" class="bpe-step" data-step="1" aria-label="Plus">${icon('add')}</button>
                   </div>`
                : `<p class="bpe-dlabel">${isMove ? 'Vitesse' : 'Durée de la pause'}</p>
                   <div class="bpe-opts" role="radiogroup" aria-label="${isMove ? 'Vitesse' : 'Durée de la pause'}">
                     ${options}
                     <button type="button" class="bpe-opt" role="radio" data-opt="custom">
                       <span class="bpe-radio"></span>
                       <span class="bpe-opt__text"><span class="bpe-opt__name">Personnalisé</span><span class="bpe-opt__desc">Entrez une valeur précise</span></span>
                     </button>
                   </div>
                   <div class="bpe-custom" data-custom hidden>
                     ${field('value', isMove ? 'Vitesse' : 'Pause', k.unit, customStart, k.range, !isMove)}
                     ${isMove ? field('time', 'Durée', 's', st.time, MOVE_TIME_RANGE, true) : ''}
                   </div>`
            }
            <button type="button" class="bpe-confirm bpe-confirm--${kind}" data-ok>${initial ? 'Confirmer' : 'Ajouter'}</button>
          </div>`;

        const $ = (sel) => el.querySelector(sel);
        const valueInput = $('[data-input="value"]');
        const timeInput = $('[data-input="time"]');
        const okButton = $('[data-ok]');

        /** What a number box holds: a value in range, or why not. */
        const read = (input, [lo, hi], integer) => {
          const n = parseNumber(input.value);
          if (n == null) return { ok: false };
          const v = integer ? Math.round(n) : Math.round(n * 100) / 100;
          if (v < lo) return { ok: false, low: true, v };
          return { ok: true, v: Math.min(hi, v), high: v > hi };
        };

        const message = (name, state, [lo, hi], unit) => {
          const msg = $(`[data-msg="${name}"]`);
          if (!msg) return;
          const u = unit === '×' ? 'fois' : unit;
          msg.classList.toggle('is-warning', !!state.low);
          msg.classList.toggle('is-error', !!state.high);
          msg.textContent = state.low
            ? `Minimum : ${fmt(lo)} ${u}`
            : state.high
              ? `Maximum : ${fmt(hi)} ${u}`
              : `Entre ${fmt(lo)} et ${fmt(hi)} ${u}`;
        };

        const renderChips = () => {
          const chips = $('[data-chips]');
          if (!chips) return;
          const times = presetIndex(MOVE_TIMES, st.time) < 0 ? [...MOVE_TIMES, st.time] : MOVE_TIMES;
          chips.innerHTML = times
            .map((t) => {
              const on = Math.abs(t - st.time) < 0.001;
              return `<button type="button" class="bpe-chiptime${on ? ' is-on' : ''}" role="radio" aria-checked="${on}" data-time="${t}">${fmtSeconds(t)}</button>`;
            })
            .join('');
        };

        const result = () => {
          if (st.opt !== 'custom') return { value: k.presets[st.opt], time: isMove ? st.time : null };
          const v = read(valueInput, k.range, isMove || isLoop);
          if (!v.ok) return null;
          if (isMove) {
            const t = read(timeInput, MOVE_TIME_RANGE, false);
            if (!t.ok) return null;
            return { value: v.v, time: t.v };
          }
          return { value: v.v, time: null };
        };

        const update = () => {
          el.querySelectorAll('[data-opt]').forEach((b) => {
            const on = String(st.opt) === b.dataset.opt;
            b.classList.toggle('is-on', on);
            b.setAttribute('aria-checked', on);
          });
          const custom = $('[data-custom]');
          if (custom) custom.hidden = st.opt !== 'custom';
          renderChips();
          okButton.disabled = !result();
        };

        // A number typed above the maximum is pulled back down to it; one
        // still under the minimum is flagged until it's long enough.
        const onInput = (input, range, unit, integer, name) => {
          const state = read(input, range, integer);
          if (state.high) input.value = fmt(range[1]);
          message(name, state, range, unit);
          if (name === 'time' && state.ok) st.time = state.v;
          update();
        };
        valueInput?.addEventListener('input', () => onInput(valueInput, k.range, k.unit, isMove || isLoop, 'value'));
        timeInput?.addEventListener('input', () => onInput(timeInput, MOVE_TIME_RANGE, 's', false, 'time'));

        const finish = (choice) => {
          entry.close();
          resolve(choice);
        };

        el.addEventListener('click', (e) => {
          const t = e.target;
          if (t.closest('[data-x]')) return finish(null);
          const opt = t.closest('[data-opt]');
          if (opt) {
            st.opt = opt.dataset.opt === 'custom' ? 'custom' : Number(opt.dataset.opt);
            update();
            if (st.opt === 'custom') valueInput?.focus();
            return;
          }
          const chip = t.closest('[data-time]');
          if (chip) {
            st.time = Number(chip.dataset.time);
            if (timeInput) {
              timeInput.value = fmt(st.time);
              message('time', { ok: true }, MOVE_TIME_RANGE, 's');
            }
            update();
            return;
          }
          const step = t.closest('[data-step]');
          if (step) {
            const current = read(valueInput, k.range, true);
            const base = current.ok ? current.v : current.low ? min : k.customDefault;
            valueInput.value = fmt(Math.min(max, Math.max(min, base + Number(step.dataset.step))));
            onInput(valueInput, k.range, k.unit, true, 'value');
            return;
          }
          if (t.closest('[data-ok]')) {
            const choice = result();
            if (choice) finish(choice);
          }
        });
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' && e.target.matches('input')) {
            e.preventDefault();
            const choice = result();
            if (choice) finish(choice);
          }
        });

        const entry = this.pushOverlay(el, () => finish(null));
        update();
        if (isLoop) requestAnimationFrame(() => valueInput.select());
      });
    }

    // ── Generic dialogs ─────────────────────────────────────────────────────

    confirmDialog({ icon: iconName, tone, title, body, confirm }) {
      return new Promise((resolve) => {
        const el = this.overlay();
        el.innerHTML = `
          <div class="bpe-dialog bpe-confirmbox" role="alertdialog" aria-modal="true" aria-label="${escapeHtml(title)}">
            <span class="bpe-confirmbox__icon bpe-tone--${tone}">${icon(iconName)}</span>
            <p class="bpe-dtitle">${escapeHtml(title)}</p>
            <p class="bpe-dbody">${escapeHtml(body)}</p>
            <div class="bpe-dbuttons">
              <button type="button" class="bpe-btn bpe-btn--secondary" data-no>Annuler</button>
              <button type="button" class="bpe-btn bpe-btn--${tone}" data-yes>${escapeHtml(confirm)}</button>
            </div>
          </div>`;
        const finish = (ok) => {
          entry.close();
          resolve(ok);
        };
        el.querySelector('[data-no]').addEventListener('click', () => finish(false));
        el.querySelector('[data-yes]').addEventListener('click', () => finish(true));
        const entry = this.pushOverlay(el, () => finish(false));
      });
    }

    nameDialog({ title, iconName, confirm, initial }) {
      return new Promise((resolve) => {
        const el = this.overlay();
        el.innerHTML = `
          <div class="bpe-dialog bpe-namebox" role="dialog" aria-modal="true" aria-label="${escapeHtml(title)}">
            <div class="bpe-namebox__head"><span class="bpe-namebox__icon">${icon(iconName)}</span><p class="bpe-dtitle">${escapeHtml(title)}</p></div>
            <input type="text" class="bpe-textinput" maxlength="${NAME_MAX}" placeholder="Nom de la séquence" autocomplete="off" data-name-input>
            <div class="bpe-dbuttons">
              <button type="button" class="bpe-btn bpe-btn--secondary" data-no>Annuler</button>
              <button type="button" class="bpe-btn bpe-btn--primary" data-yes>${escapeHtml(confirm)}</button>
            </div>
          </div>`;
        const input = el.querySelector('[data-name-input]');
        const yes = el.querySelector('[data-yes]');
        input.value = initial;
        const sync = () => (yes.disabled = !this.cleanName(input.value));
        const finish = (name) => {
          entry.close();
          resolve(name);
        };
        input.addEventListener('input', sync);
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' && this.cleanName(input.value)) {
            e.preventDefault();
            finish(this.cleanName(input.value));
          }
        });
        el.querySelector('[data-no]').addEventListener('click', () => finish(null));
        yes.addEventListener('click', () => finish(this.cleanName(input.value) || null));
        const entry = this.pushOverlay(el, () => finish(null));
        sync();
        requestAnimationFrame(() => input.select());
      });
    }

    async rename() {
      const name = await this.nameDialog({
        title: 'Renommer la séquence',
        iconName: 'edit',
        confirm: 'Renommer',
        initial: this.name,
      });
      if (!name) return;
      this.name = name;
      this.changed();
    }

    toast(message, tone = 'info') {
      const el = document.createElement('div');
      el.className = `bpe-toast bpe-tone--${tone}`;
      el.innerHTML = `${icon(tone === 'success' ? 'check' : tone === 'danger' ? 'error' : 'info')}<span>${escapeHtml(message)}</span>`;
      this.toasts.replaceChildren(el);
      setTimeout(() => {
        el.classList.add('is-leaving');
        setTimeout(() => el.remove(), 250);
      }, 2600);
    }

    // ── Files: the saved-pattern library (kept in this browser) ─────────────

    library() {
      const lib = storage.get(LIBRARY_KEY);
      return lib && typeof lib === 'object' && lib.patterns && typeof lib.patterns === 'object' ? lib : { patterns: {} };
    }

    showFiles() {
      const el = this.overlay('bpe-overlay--sheet');
      const option = (key, iconName, tone, label, sub) =>
        `<button type="button" class="bpe-fileopt bpe-fileopt--${tone}" data-file="${key}">
          <span class="bpe-fileopt__icon">${icon(iconName)}</span>
          <span class="bpe-fileopt__text"><span class="bpe-fileopt__label">${label}</span><span class="bpe-fileopt__sub">${sub}</span></span>
          ${icon('chevron', 'bpe-fileopt__chev')}
        </button>`;
      el.innerHTML = `
        <div class="bpe-sheet" role="dialog" aria-modal="true" aria-label="Fichiers">
          <span class="bpe-sheet__handle" aria-hidden="true"></span>
          <p class="bpe-sheet__title">FICHIERS</p>
          ${option('open', 'folderOpen', 'blue', 'Ouvrir une séquence', 'Charger une séquence enregistrée')}
          ${option('save', 'save', 'sky', 'Enregistrer la séquence', 'Garder une copie dans ce navigateur')}
        </div>`;
      const entry = this.pushOverlay(el, () => entry.close());
      el.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-file]');
        if (!btn) return;
        entry.close();
        if (btn.dataset.file === 'open') this.openLibrary();
        else this.saveToLibrary();
      });
    }

    async saveToLibrary() {
      if (!this.blocks.length) {
        this.toast('Rien à enregistrer : la séquence est vide.');
        return;
      }
      const name = await this.nameDialog({
        title: 'Enregistrer la séquence',
        iconName: 'save',
        confirm: 'Enregistrer',
        initial: this.name,
      });
      if (!name) return;
      const lib = this.library();
      // Saving under a name that's already taken replaces that pattern — ask
      // first rather than silently overwriting it.
      if (lib.patterns[name]) {
        const ok = await this.confirmDialog({
          icon: 'warning',
          tone: 'warning',
          title: `Remplacer « ${name} »?`,
          body: 'Une séquence porte déjà ce nom. Elle sera remplacée par celle-ci.',
          confirm: 'Remplacer',
        });
        if (!ok) return;
      }
      lib.patterns[name] = { json: this.toJson(name), savedAt: Date.now() };
      if (!storage.set(LIBRARY_KEY, lib)) {
        this.toast("Impossible d'enregistrer dans ce navigateur.", 'danger');
        return;
      }
      this.name = name;
      this.changed();
      this.toast(`« ${name} » enregistrée`, 'success');
    }

    openLibrary() {
      const el = this.overlay('bpe-overlay--panel');
      const panelEntry = this.pushOverlay(el, () => panelEntry.close());
      const draw = () => {
        const entries = Object.entries(this.library().patterns).sort((a, b) => (b[1].savedAt || 0) - (a[1].savedAt || 0));
        const items = entries
          .map(([name, item]) => {
            let meta = '';
            try {
              const blocks = blocksFromJson(item.json);
              meta = `${countBlocks(blocks)} bloc${countBlocks(blocks) > 1 ? 's' : ''} · cycle de ${fmtDuration(cycleSeconds(blocks))}`;
            } catch (_) {
              meta = 'Fichier illisible';
            }
            const date = item.savedAt
              ? new Date(item.savedAt).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' })
              : '';
            return `<li class="bpe-libitem">
              <button type="button" class="bpe-libitem__open" data-load="${escapeHtml(name)}">
                <span class="bpe-libitem__icon">${icon('folder')}</span>
                <span class="bpe-libitem__text"><span class="bpe-libitem__name">${escapeHtml(name)}</span><span class="bpe-libitem__meta">${meta}${date ? ` · ${date}` : ''}</span></span>
              </button>
              <button type="button" class="bpe-libitem__del" data-remove="${escapeHtml(name)}" aria-label="Supprimer « ${escapeHtml(name)} »">${icon('delete')}</button>
            </li>`;
          })
          .join('');
        el.innerHTML = `
          <div class="bpe-panel" role="dialog" aria-modal="true" aria-label="Mes séquences">
            <div class="bpe-panel__bar">
              <div class="bpe-status" aria-hidden="true">
                <span class="bpe-status__time">${escapeHtml(this.screen.querySelector('[data-clock]')?.textContent || '')}</span>
                <span class="bpe-status__icons">${STATUS_ICONS}</span>
              </div>
              <div class="bpe-panel__toolbar">
                <button type="button" class="bpe-panel__back" data-back aria-label="Retour">${icon('back')}</button>
                <p class="bpe-panel__title">Mes séquences</p>
              </div>
            </div>
            ${
              items
                ? `<ul class="bpe-liblist">${items}</ul>`
                : `<div class="bpe-empty bpe-empty--panel"><span class="bpe-empty__icon">${icon('folder')}</span><p class="bpe-empty__title">Aucune séquence enregistrée</p><p class="bpe-empty__text">Utilisez Fichiers › Enregistrer pour garder vos séquences dans ce navigateur.</p></div>`
            }
          </div>`;
      };
      draw();
      el.addEventListener('click', async (e) => {
        if (e.target.closest('[data-back]')) return panelEntry.close();
        const load = e.target.closest('[data-load]');
        const del = e.target.closest('[data-remove]');
        if (load) {
          const name = load.dataset.load;
          const item = this.library().patterns[name];
          let blocks;
          try {
            blocks = blocksFromJson(item?.json);
          } catch (_) {
            this.toast("Cette séquence n'a pas pu être ouverte.", 'danger');
            return;
          }
          if (this.blocks.length) {
            const ok = await this.confirmDialog({
              icon: 'warning',
              tone: 'warning',
              title: 'Remplacer la séquence actuelle?',
              body: 'Les blocs en cours de modification seront remplacés par la séquence ouverte.',
              confirm: 'Remplacer',
            });
            if (!ok) return;
          }
          panelEntry.close();
          this.replaceAll(name, blocks);
          this.toast('Séquence ouverte', 'success');
        } else if (del) {
          const name = del.dataset.remove;
          const ok = await this.confirmDialog({
            icon: 'delete',
            tone: 'danger',
            title: `Supprimer « ${name} »?`,
            body: 'Cette séquence enregistrée sera supprimée de ce navigateur.',
            confirm: 'Supprimer',
          });
          if (!ok) return;
          const lib = this.library();
          delete lib.patterns[name];
          storage.set(LIBRARY_KEY, lib);
          draw();
        }
      });
    }

    // ── Sending to the Brimbéloi team (Shopify contact form) ────────────────

    setupForm() {
      this.form = this.querySelector('form.bpe-form');
      this.summaryEl = this.querySelector('[data-summary]');
      this.codeEl = this.querySelector('[data-code]');
      this.formError = this.querySelector('[data-form-error]');
      this.updateSummary();
      if (!this.form) return;
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.submitLabel = this.submitButton?.innerHTML;

      // Listens on window in the capture phase so it runs before Shopify's
      // own submit handlers (storefront hCaptcha): the hidden fields are
      // filled synchronously, then the submit carries on untouched, so
      // Shopify adds its CAPTCHA token as for any contact form.
      this._onSubmit = (e) => {
        if (e.target !== this.form) return;
        if (!this.blocks.length) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.showFormError('Ajoutez au moins un bloc à votre séquence avant de l’envoyer.');
          this.toast('Votre séquence est vide.', 'danger');
          return;
        }
        this.showFormError('');
        this.fillHiddenFields();
        this.flush();
        if (this.submitButton) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('is-loading');
          this.submitButton.innerHTML = '<span>Envoi en cours…</span>';
          // Should the page not navigate away (CAPTCHA closed…), the button
          // comes back.
          clearTimeout(this._unlock);
          this._unlock = setTimeout(() => this._onShow(), 20000);
        }
      };
      window.addEventListener('submit', this._onSubmit, true);
      // Coming back to the page (back button) — the form is usable again.
      this._onShow = () => {
        if (this.submitButton) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('is-loading');
          this.submitButton.innerHTML = this.submitLabel;
        }
      };
      window.addEventListener('pageshow', this._onShow);

      const status = this.querySelector('[data-bpe-status]');
      if (status) {
        requestAnimationFrame(() => {
          status.scrollIntoView({ block: 'center' });
          status.focus({ preventScroll: true });
        });
        // The green "sent" message goes away by itself after 10 seconds.
        if (status.classList.contains('bpe-send__status--ok')) {
          setTimeout(() => {
            status.classList.add('is-hiding');
            setTimeout(() => status.remove(), 400);
          }, 10000);
        }
      }
    }

    // ── Sequence tester ─────────────────────────────────────────────────────
    // Plays the Jiggeur video along the sequence: a Move plays it at its
    // speed (relative to the speed the video shows), a Wait pauses it, a Loop goes
    // through its blocks once per repeat. The block being played lights up
    // in the phone.

    setupTester() {
      this.testButton = this.querySelector('[data-test-button]');
      if (!this.testButton) return;
      this.testLabel = this.testButton.querySelector('[data-test-label]');
      this.testStatus = this.querySelector('[data-test-status]');
      this.testRepeat = this.querySelector('[data-repeat]')?.dataset.repeat !== 'false';
      const videoSpeed = Number(this.querySelector('[data-video-speed]')?.dataset.videoSpeed);
      this.testVideoSpeed = videoSpeed >= 1 && videoSpeed <= 100 ? videoSpeed : 100;
      this.video = this.querySelector('.bpe-video__frame video');
      if (this.video) {
        this.video.muted = true;
        this.video.loop = true;
        this.video.playsInline = true;
        this.video.setAttribute('playsinline', '');
        this.video.removeAttribute('autoplay');
        this.video.pause();
      }
      this.testButton.addEventListener('click', () => (this.testRun ? this.stopTest() : this.startTest()));
      this.showTestIdle();
      this.updateTester();
      if (this.querySelector('[data-autostart]')?.dataset.autostart === 'true') this.autostartTest();
    }

    /**
     * Starts the test by itself the first time the phone and video come
     * into view, so a visitor sees the Jiggeur move right away. The video
     * is muted, which browsers let play without a tap.
     */
    autostartTest() {
      const stage = this.querySelector('.bpe-stage') || this;
      const start = () => {
        if (!this.testRun && this.blocks.length && this.isConnected) this.startTest();
      };
      if (!('IntersectionObserver' in window)) return start();
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          observer.disconnect();
          start();
        },
        { threshold: 0.2 }
      );
      observer.observe(stage);
    }

    updateTester() {
      if (!this.testButton || this.testRun) return;
      this.testButton.disabled = !this.blocks.length;
      if (this.testStatus && !this.testMessage) this.showTestIdle();
    }

    showTestIdle(message = '') {
      if (!this.testStatus) return;
      this.testMessage = message;
      this.testStatus.innerHTML = `<p class="bpe-video__idle">${escapeHtml(
        message ||
          (this.blocks.length
            ? 'Appuyez sur « Tester la séquence » pour voir le Jiggeur suivre votre séquence.'
            : 'Ajoutez des blocs à votre séquence, puis testez-la ici.')
      )}</p>`;
    }

    async startTest() {
      if (this.testRun || !this.blocks.length) return;
      const run = { stopped: false };
      this.testRun = run;
      this.testMessage = '';
      this.testButton.classList.add('is-running');
      this.testButton.setAttribute('aria-pressed', 'true');
      if (this.testLabel) this.testLabel.textContent = 'Arrêter le test';
      this.querySelector('.bpe-video')?.classList.add('is-running');
      let cycle = 0;
      do {
        cycle++;
        let steps = 0;
        for (const step of runSteps(this.blocks)) {
          if (run.stopped) return;
          steps++;
          await this.playStep(step, run, cycle);
        }
        if (run.stopped) return;
        // Only empty loops: nothing to play, and repeating would spin.
        if (!steps) return this.stopTest('Votre séquence ne contient aucune action ni attente à tester.');
      } while (this.testRepeat);
      this.stopTest('Séquence terminée. Appuyez sur « Tester la séquence » pour la rejouer.');
    }

    stopTest(message = '') {
      const run = this.testRun;
      if (!run) return;
      run.stopped = true;
      this.testRun = null;
      clearTimeout(this._testTimer);
      this._testDone?.();
      if (this.video) this.rampVideo(this.video, 0, RAMP_MS);
      this.setRunning(null);
      if (!this.testButton) return;
      this.testButton.classList.remove('is-running');
      this.testButton.setAttribute('aria-pressed', 'false');
      if (this.testLabel) this.testLabel.textContent = this.testLabel.dataset.idle || 'Tester la séquence';
      this.querySelector('.bpe-video')?.classList.remove('is-running');
      this.showTestIdle(message);
      this.updateTester();
    }

    async playStep({ block, loops }, run, cycle) {
      const v = this.video;
      if (v) {
        // The video shows the Jiggeur at testVideoSpeed %: a slowed-down
        // (slow-motion) video keeps enough frames for the slow speeds, and
        // is sped up for the fast ones. A Wait brings it to a stop. Either
        // way it eases there over the start of the block, like the motor.
        const target = block.type === 'move' ? block.speed / this.testVideoSpeed : 0;
        this.rampVideo(v, target, Math.min(RAMP_MS, block.duration * 1000));
      }
      this.setRunning(block, loops);
      this.showTestStep(block, loops, cycle);
      await this.testSleep(block.duration * 1000, run);
    }

    /**
     * Sets how fast the video plays. Browsers accept about 0.0625x to 16x,
     * and some refuse the top of that range: then the fastest they take is
     * used instead.
     */
    setVideoRate(v, rate) {
      for (const r of [Math.min(16, Math.max(0.0625, rate)), 8, 4, 2, 1]) {
        if (r > rate && r !== 1) continue;
        try {
          v.defaultPlaybackRate = r;
          v.playbackRate = r;
          return;
        } catch (_) {}
      }
    }

    /**
     * Eases the video from the speed it's playing at now to [target] (0 =
     * stopped) over [ms], instead of jumping — speeding up, slowing down or
     * coming to a halt the way the motor does. this.videoRate is the speed
     * it's at (0 while paused); a new ramp starts from wherever the last
     * one got to.
     */
    rampVideo(v, target, ms) {
      cancelAnimationFrame(this._rampRaf);
      const from = v.paused ? 0 : this.videoRate ?? v.playbackRate;
      const finish = () => {
        this.videoRate = target;
        if (target > 0) this.setVideoRate(v, target);
        else v.pause();
      };
      if (Math.abs(from - target) < 0.001 || ms <= 0) {
        if (target > 0 && v.paused) {
          this.setVideoRate(v, target);
          v.play()?.catch(() => {});
        }
        return finish();
      }
      if (target > 0 && v.paused) {
        this.setVideoRate(v, MIN_RATE);
        v.play()?.catch(() => {});
      }
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / ms);
        // Ease in and out, so the change has no sudden start or end.
        const eased = t * t * (3 - 2 * t);
        const rate = from + (target - from) * eased;
        this.videoRate = rate;
        if (t >= 1) return finish();
        // Browsers can't go slower than MIN_RATE: hold it there until the
        // pause at the end.
        this.setVideoRate(v, Math.max(MIN_RATE, rate));
        this._rampRaf = requestAnimationFrame(step);
      };
      this._rampRaf = requestAnimationFrame(step);
    }

    testSleep(ms, run) {
      return new Promise((resolve) => {
        if (run.stopped) return resolve();
        this._testDone = resolve;
        this._testTimer = setTimeout(resolve, ms);
      });
    }

    /** Lights up the block being played, and the loops around it. */
    setRunning(block, loops = []) {
      this.runningIds = block ? new Set([block.id, ...loops.map((l) => l.loop.id)]) : null;
      for (const [id, el] of this.rowEls) {
        const blockId = id.startsWith('b') ? Number(id.slice(1)) : null;
        el.classList.toggle(
          'is-running',
          !!(blockId && this.runningIds?.has(blockId) && !el.classList.contains('is-ghost'))
        );
      }
      if (!block || this.drag || this.overlays.length) return;
      // Keep it in view in the phone: the block itself, or its loop when
      // the loop is closed.
      const shown = [block, ...loops.map((l) => l.loop).reverse()].find((b) => this.rowEls.has(`b${b.id}`));
      if (shown) this.revealRow(`b${shown.id}`);
    }

    showTestStep(block, loops, cycle) {
      if (!this.testStatus) return;
      const what = block.type === 'move' ? `Action · ${Math.trunc(block.speed)} %` : 'Attente';
      const rounds = loops
        .map(({ loop, round }) => `<span class="bpe-run__loop">Boucle ${round} / ${loop.count}</span>`)
        .join('');
      this.testStatus.innerHTML = `
        <div class="bpe-run bpe-run--${block.type}">
          <div class="bpe-run__row">
            <span class="bpe-run__badge">${icon(KIND[block.type].icon)}</span>
            <span class="bpe-run__what">${what}</span>
            <span class="bpe-run__time">${fmtSeconds(block.duration)}</span>
          </div>
          <div class="bpe-run__bar"><span style="--run-ms: ${Math.round(block.duration * 1000)}ms"></span></div>
          <div class="bpe-run__meta">${rounds}${this.testRepeat ? `<span>Cycle ${cycle}</span>` : ''}</div>
        </div>`;
      // Restart the bar's fill for this step.
      const bar = this.testStatus.querySelector('.bpe-run__bar span');
      void bar.offsetWidth;
      bar.classList.add('is-filling');
    }

    showFormError(text) {
      if (!this.formError) return;
      this.formError.textContent = text;
      this.formError.hidden = !text;
    }

    /**
     * Download link of the sequence as a .txt file in the app's own file
     * format (toJson). The pattern rides in the link itself and the backend
     * (data-endpoint, see the section) only hands it back as a file —
     * nothing to call at send time, so nothing that can fail then.
     */
    textFileUrl() {
      const endpoint = this.dataset.endpoint;
      if (!endpoint) return 'Non disponible — voir « Fichier de la séquence JSON » plus bas';
      return `${endpoint.replace(/\/+$/, '')}.txt?p=${encodeURIComponent(JSON.stringify(this.toJson()))}`;
    }

    setField(key, value) {
      const input = this.form.querySelector(`[data-field="${key}"]`);
      if (input) input.value = value;
    }

    fillHiddenFields() {
      const set = (key, value) => this.setField(key, value);
      const code = encodeBlocks(this.blocks);
      set('kind', 'Séquence Jiggeur');
      set('name', this.name);
      set('code', code);
      set('file', this.textFileUrl());
      set('cycle', fmtDuration(cycleSeconds(this.blocks)));
      set('count', String(countBlocks(this.blocks)));
      set('detail', describeBlocks(this.blocks).join('\n'));
      set('json', JSON.stringify(this.toJson()));
    }

    updateSummary() {
      if (!this.summaryEl && !this.codeEl) return;
      const n = countBlocks(this.blocks);
      if (this.summaryEl) {
        this.summaryEl.textContent = n
          ? `« ${this.name} » · ${n} bloc${n > 1 ? 's' : ''} · cycle de ${fmtDuration(cycleSeconds(this.blocks))}${hasEmptyLoop(this.blocks) ? ' · attention : une boucle est vide' : ''}`
          : 'Votre séquence est vide pour l’instant.';
      }
      if (this.codeEl) this.codeEl.textContent = encodeBlocks(this.blocks) || '—';
      if (n) this.showFormError('');
    }
  }

  customElements.define('brimbeloi-pattern-editor', PatternEditor);
})();
